import { useEffect, useState } from 'preact/hooks';
import ROUTES from './routes.json';

// The routes (brief, "Website"): /, /2023-2024, /item/119-hr-7148, /member/{bioguide_id},
// /how-this-works, /corrections, /who-pays, /privacy — plus /support and /report from the
// design. The design's /states/{code} pages are not in v1 (Dan, 21 Sep 2026): the States
// menu stays, and each state opens Support Us. Plain history API; no router library needed.
//
// THE LIST ITSELF IS routes.json, which the site's server reads too: it answers 404 for any
// address that file does not match, so the status a link unfurler gets and the page a visitor
// sees come from one list and cannot drift apart.

const EVENT = 'sr:navigate';

export function navigate(path: string, opts: { replace?: boolean; state?: Record<string, unknown> } = {}) {
  if (path === here()) return;
  // Entries the site pushed are marked, so closing a modal can step back through them
  // rather than stacking a new one on top (see closeOverlay). An entry opened from the grid says
  // so, for the phone's way back ("All votes"); `state` adds what a screen needs (a phone's member
  // page opened from "Your representatives" shows the three as tabs).
  const state = { sr: true, ...(onGrid() ? { grid: true } : {}), ...opts.state };
  if (opts.replace) history.replaceState(state, '', path);
  else history.pushState(state, '', path);
  window.dispatchEvent(new Event(EVENT));
}

/** The grid is on screen: a term's page with no item or card open over it. */
function onGrid() {
  const r = parse(location.pathname);
  return r.page === 'home' && !r.item && !r.member;
}

type Entry = { sr?: boolean; grid?: boolean; reps?: boolean; scroll?: number; sheet?: SheetName } | null;
const entry = () => history.state as Entry;

/** The phone's item and member pages step with ‹ › (Design's phone handoff): the entry is REPLACED,
 *  so Back does not walk through every item, and keeps where it came from; a new item starts at
 *  its top. */
export function stepTo(path: string, state: Record<string, unknown> = {}) {
  if (path === here()) return;
  const { scroll: _, ...keep } = entry() ?? {};
  history.replaceState({ ...keep, ...state }, '', path);
  window.dispatchEvent(new Event(EVENT));
}

/** How far down a phone page was, kept in its own history entry so Back returns there. */
export function saveScroll(y: number) {
  history.replaceState({ ...(entry() ?? {}), scroll: Math.round(y) }, '', here());
}
export const savedScroll = () => entry()?.scroll ?? 0;
/** The phone page's way back says where it goes: to the grid, or back a screen. */
export const backLabel = () => (!entry()?.sr || entry()?.grid ? 'All votes' : 'Back');
/** A phone's member page opened from "Your representatives". */
export const fromReps = () => !!entry()?.reps;

/** Close a modal or card: back to wherever it was opened from, or — when the page was
 *  opened straight at it, from a shared link — to the grid behind it. */
export function closeOverlay(fallback: string) {
  if ((history.state as { sr?: boolean } | null)?.sr) history.back();
  else navigate(fallback, { replace: true });
}

// THE PHONE'S SHEETS (Design's phone handoff, "Sheets"): Find, Who represents me?, Term items and key,
// the months, the menu, and the States list. A sheet is not an address, but the phone's Back must close
// it, so an open sheet is a history entry of its own at the same address, named in its state. Closing
// one steps back off that entry; leaving one for another page REPLACES it, so Back from that page
// returns to the screen under the sheet, not to the sheet.
export type SheetName = 'find' | 'lookup' | 'options' | 'months' | 'menu' | 'states' | 'jump';
const here = () => location.pathname + location.search + location.hash;
const sheetNow = (): SheetName | null => (history.state as { sheet?: SheetName } | null)?.sheet ?? null;

export function openSheet(name: SheetName) {
  if (sheetNow()) history.replaceState({ sr: true, sheet: name }, '', here());
  else history.pushState({ sr: true, sheet: name }, '', here());
  window.dispatchEvent(new Event(EVENT));
}

/** Close the open sheet; `then` runs once the page under it is back (the browser restores its
 *  scroll position on the way, so anything that scrolls must wait for that). */
export function closeSheet(then?: () => void) {
  if (!sheetNow()) { then?.(); return; }
  if (then) window.addEventListener('popstate', () => requestAnimationFrame(then), { once: true });
  history.back();
}

/** Leave the sheet for another page. */
export function leaveSheet(path: string) {
  if (!sheetNow()) { navigate(path); return; }
  if (path === here()) { closeSheet(); return; }
  history.replaceState({ sr: true, ...(onGrid() ? { grid: true } : {}) }, '', path);
  window.dispatchEvent(new Event(EVENT));
}

/** Change the page under an open sheet and keep the sheet open (a term picked in Term, items, key):
 *  the sheet's entry becomes the new page's, and the sheet gets a new one on top, so Done returns to
 *  the new term and Back from there to the old one. */
export function navigateUnderSheet(path: string) {
  const s = sheetNow();
  if (!s) { navigate(path); return; }
  if (path === here()) return;
  history.replaceState({ sr: true }, '', path);
  history.pushState({ sr: true, sheet: s }, '', path);
  window.dispatchEvent(new Event(EVENT));
}

export function useSheet(): SheetName | null {
  const [sheet, setSheet] = useState(sheetNow);
  useEffect(() => {
    const update = () => setSheet(sheetNow());
    window.addEventListener('popstate', update);
    window.addEventListener(EVENT, update);
    return () => { window.removeEventListener('popstate', update); window.removeEventListener(EVENT, update); };
  }, []);
  return sheet;
}

export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const update = () => setPath(location.pathname);
    window.addEventListener('popstate', update);
    window.addEventListener(EVENT, update);
    return () => { window.removeEventListener('popstate', update); window.removeEventListener(EVENT, update); };
  }, []);
  return path;
}

export type Route =
  | { page: 'home'; term: string | null; item: string | null; member: string | null }
  | { page: 'how' | 'corrections' | 'pays' | 'privacy' | 'support' | 'report' }
  | { page: 'state' }
  | { page: 'missing' };

type Fixed = 'home' | 'how' | 'corrections' | 'pays' | 'privacy' | 'support' | 'report';
const PAGES = ROUTES.pages as Record<string, Fixed>;
const PATTERNS = Object.entries(ROUTES.patterns).map(([kind, re]) => [kind, new RegExp(re)] as const);

export function parse(path: string): Route {
  const p = path.replace(/\/+$/, '') || '/';
  const fixed = PAGES[p];
  if (fixed === 'home') return { page: 'home', term: null, item: null, member: null };
  if (fixed) return { page: fixed };
  for (const [kind, re] of PATTERNS) {
    const m = p.match(re);
    if (!m) continue;
    if (kind === 'term') return { page: 'home', term: m[1], item: null, member: null };
    if (kind === 'item') return { page: 'home', term: null, item: m[1], member: null };
    if (kind === 'member') return { page: 'home', term: null, item: null, member: m[1] };
    if (kind === 'state') return { page: 'state' };   // the design's state address: opens Support Us
  }
  return { page: 'missing' };
}
